import jsPDF from "jspdf";

const MARGIN = 56;

// -----------------------------------------------------------------------------
// Font loading — Cormorant Garamond (serif, matches website headings) +
// Outfit (sans, matches website body). Loaded once, cached, and registered
// with jsPDF's virtual filesystem on each new doc.
// -----------------------------------------------------------------------------
let _fontCache = null;

async function _fetchFontAsBase64(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Font fetch failed: ${url}`);
  const buf = await res.arrayBuffer();
  // Chunked conversion to avoid stack overflow on large font files.
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function _loadFonts() {
  if (_fontCache) return _fontCache;
  const [cormorant, cormorantItalic, outfit] = await Promise.all([
    _fetchFontAsBase64("/fonts/CormorantGaramond.ttf"),
    _fetchFontAsBase64("/fonts/CormorantGaramond-Italic.ttf"),
    _fetchFontAsBase64("/fonts/Outfit.ttf"),
  ]);
  _fontCache = { cormorant, cormorantItalic, outfit };
  return _fontCache;
}

function _registerFonts(doc, fonts) {
  doc.addFileToVFS("CormorantGaramond.ttf", fonts.cormorant);
  doc.addFont("CormorantGaramond.ttf", "Cormorant", "normal");
  doc.addFont("CormorantGaramond.ttf", "Cormorant", "bold");

  doc.addFileToVFS("CormorantGaramond-Italic.ttf", fonts.cormorantItalic);
  doc.addFont("CormorantGaramond-Italic.ttf", "Cormorant", "italic");

  doc.addFileToVFS("Outfit.ttf", fonts.outfit);
  doc.addFont("Outfit.ttf", "Outfit", "normal");
  doc.addFont("Outfit.ttf", "Outfit", "bold");
}

// -----------------------------------------------------------------------------
// Doc helpers
// -----------------------------------------------------------------------------
async function newDoc() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const fonts = await _loadFonts();
  _registerFonts(doc, fonts);
  return doc;
}

function wrapText(doc, text, maxWidth) {
  return doc.splitTextToSize(text || "", maxWidth);
}

// Fonts:
//   Cormorant Garamond — only for the elegant H1 title on page 1 (large size masks its thin strokes)
//   All other text     — jsPDF built-in Helvetica (guaranteed crisp black rendering in every PDF viewer)
function _pick(opts) {
  // Italic accents on serif requests use Cormorant italic (small role);
  // all other roles use Helvetica which renders as solid, unambiguous black.
  if (opts.serif && opts.italic) return ["Cormorant", "italic"];
  return ["helvetica", opts.bold ? "bold" : "normal"];
}

function addHeader(doc, title, subtitle) {
  // Title in Cormorant bold for visual identity — big enough that thinness reads as elegant, not grey
  doc.setFont("Cormorant", "bold");
  doc.setFontSize(34);
  doc.setTextColor(0, 0, 0);
  doc.text(title, MARGIN, MARGIN + 10);
  if (subtitle) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(subtitle, MARGIN, MARGIN + 34);
  }
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, MARGIN + 48, 595 - MARGIN, MARGIN + 48);
  return MARGIN + 72;
}

function addBody(doc, text, y, opts = {}) {
  const size = opts.size || 12;
  const [family, style] = _pick(opts);
  doc.setFont(family, style);
  doc.setFontSize(size);
  doc.setTextColor(...(opts.color || [0, 0, 0]));
  const lines = wrapText(doc, text, 595 - MARGIN * 2);
  lines.forEach((ln) => {
    if (y > 800) { doc.addPage(); y = MARGIN; }
    doc.text(ln, MARGIN, y);
    y += size + 6;
  });
  return y + 6;
}

const LETTER_PROMPTS_FOR_PDF = [
  { name: "how_much_you_mean", label: "What I want you to know about how much you mean to me…" },
  { name: "favourite_memory", label: "My favourite memory with you is…" },
  { name: "grateful_never_said", label: "Something I'm grateful to you for, that I may never have said out loud, is…" },
  { name: "remember_this_about_you", label: "If you ever doubt yourself, I want you to remember this about you…" },
  { name: "changed_my_life", label: "One way you've changed my life for the better is…" },
  { name: "three_wishes", label: "Here are three wishes I have for your future…" },
  { name: "hard_day_words", label: "If I could be right next to you on a hard day, this is what I'd say…" },
  { name: "how_i_hope_youll_remember_me", label: "When you think of me, I hope you remember…" },
];

function addFooterToAllPages(doc, user) {
  const totalPages = doc.internal.getNumberOfPages();
  const name = user?.name || "";
  const email = user?.email || "";
  const stamp = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Hairline divider
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, 802, 595 - MARGIN, 802);

    // Left: name + email
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(name, MARGIN, 815);
    if (email) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text(email, MARGIN, 826);
    }

    // Centre: "Personal use only" copyright
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Personal use only  \u00b7  \u00a9 At The End of The Day", 297, 820, { align: "center" });

    // Right: timestamp + page number
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(stamp, 595 - MARGIN, 815, { align: "right" });
    doc.text(`Page ${i} of ${totalPages}`, 595 - MARGIN, 826, { align: "right" });
  }
}

const WISHES_LABELS = {
  burial_or_cremation: "Burial, cremation, or something else",
  ceremony: "The feel of the ceremony",
  speakers: "People you'd like to be involved",
  music_readings: "Music, readings, and personal touches",
  flowers_donations: "Flowers, donations, and dress code",
  after_ceremony: "After the ceremony",
  other_wishes: "Any other wishes",
};

export async function exportPlanPdf(user, plan) {
  const doc = await newDoc();
  const fullName = (plan?.sections?.personal?.full_name || "").trim() || user?.name || "you";
  let y = addHeader(doc, "My Plan", `Prepared by ${fullName} · ${new Date().toLocaleDateString("en-GB")}`);

  const sections = plan?.sections || {};
  const order = ["personal", "executors", "will", "accounts", "digital", "utilities", "insurance", "other_assets", "wishes", "letters"];
  const labels = {
    personal: "Personal details",
    executors: "Executors & key contacts",
    will: "Will & legal documents",
    accounts: "Bank accounts & finances",
    digital: "Digital accounts",
    utilities: "Utilities",
    insurance: "Insurance & pensions",
    other_assets: "Other Assets",
    wishes: "Funeral wishes",
    letters: "Letters to loved ones",
  };
  let sectionIndex = 0;
  order.forEach((k) => {
    const data = sections[k];
    if (!data) return;

    if (k === "letters") {
      // Only include letters that have any content
      const written = [1, 2, 3, 4].filter((n) => {
        const recipient = (data[`letter${n}_recipient`] || "").trim();
        const anyPrompt = LETTER_PROMPTS_FOR_PDF.some((p) => (data[`letter${n}_${p.name}`] || "").trim().length > 0);
        return recipient || anyPrompt;
      });
      if (written.length === 0) return;

      written.forEach((n) => {
        doc.addPage();
        let ly = MARGIN;
        const recipient = (data[`letter${n}_recipient`] || "").trim();
        ly = addBody(doc, `Letter ${n} \u2014 To ${recipient || "\u2026"}`, ly, {
          size: 22, italic: true, serif: true, color: [0, 0, 0],
        });
        ly += 8;
        LETTER_PROMPTS_FOR_PDF.forEach((p) => {
          const val = (data[`letter${n}_${p.name}`] || "").trim();
          if (!val) return;
          ly = addBody(doc, val, ly, { size: 12, color: [0, 0, 0] });
          ly += 6;
        });
      });
      return;
    }

    if (sectionIndex > 0) doc.addPage();
    let sy = sectionIndex === 0 ? y : MARGIN;
    sy = addBody(doc, labels[k], sy, { size: 22, serif: true, bold: true, color: [0, 0, 0] });
    sy += 6;
    Object.entries(data).forEach(([field, val]) => {
      if (!val) return;
      let label;
      if (k === "wishes" && WISHES_LABELS[field]) {
        label = WISHES_LABELS[field];
      } else {
        const raw = field.replace(/_/g, " ");
        label = raw.charAt(0).toUpperCase() + raw.slice(1);
      }
      sy = addBody(doc, `${label}:`, sy, { size: 10, bold: true, color: [0, 0, 0] });
      sy = addBody(doc, String(val), sy, { size: 12 });
    });
    sectionIndex += 1;
  });

  if (!Object.keys(sections).length) {
    y = addBody(doc, "You haven't added anything to your plan yet. Return to the Plan Builder to begin.", y);
  }

  _finalisePdf(doc, user, `end-of-day-plan-${new Date().toISOString().slice(0, 10)}.pdf`);
}

function _finalisePdf(doc, user, filename) {
  addFooterToAllPages(doc, user);
  doc.save(filename);
}

export async function exportChapterPdf(track, chapter, user) {
  const doc = await newDoc();
  let y = addHeader(doc, chapter.title, track === "bereaved" ? "If you've been bereaved" : "Preparing for your family");
  y = addBody(doc, chapter.summary || "", y, { size: 12, italic: true, serif: true, color: [0, 0, 0] });

  chapter.sections.forEach((s) => {
    y = addBody(doc, s.heading, y + 8, { size: 15, serif: true, bold: true, color: [0, 0, 0] });
    y = addBody(doc, s.body, y);
  });

  if (chapter.checklist?.length) {
    y = addBody(doc, "Checklist", y + 8, { size: 15, serif: true, bold: true, color: [0, 0, 0] });
    chapter.checklist.forEach((c) => {
      y = addBody(doc, `\u2610  ${c}`, y);
    });
  }

  _finalisePdf(doc, user, `end-of-day-${chapter.id}.pdf`);
}
